import React, {useMemo} from "react";
import {Col, Form, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import * as yup from "yup";
import {useFormik} from "formik";
import LabelInput from "../../../components/Common/LabelInput";
import {addRole, modalRoles, updRoles} from "src/store/actions";
import PermissionsTable from "./PermissionsTable";
import {useQuery} from "@tanstack/react-query";
import {PermissionsService} from "../../../services/permissions.service";
import NoSearchResult from "../../../components/Common/NoSearchResult";
import {IPermissionGroupPagination} from "../../../store/permissions/types";

interface Props {
    isOpen: boolean;
}

interface IFormValues {
    name: string;
    permissions: number[];
}

const ModalRoles: React.FC<Props> = ({isOpen}: Props) => {
    const dispatch = useDispatch();
    const item = useSelector((state: any) => state.roles.item);

    const permissionsGroupsWithUngrouped = (resp: IPermissionGroupPagination): IPermissionGroupPagination => {
        if (resp.ungrouped.length === 0 || !resp.ungrouped) return resp

        const groups = [...resp.data, {id: -1, name: "Ungrouped", icon: "no-entry", permissions: [...resp.ungrouped]}]
        return {...resp, data: groups};
    }

    const {data: permissionsGroups, isLoading: isLoadingPermissions} = useQuery({
        queryKey: ["keyPermissionsGroups"],
        queryFn: () => PermissionsService.getGroupsAll(),
        select: permissionsGroupsWithUngrouped
    });

    const validationSchema = yup.object().shape({
        name: yup.string().required("required"),
        permissions: yup.array().min(1, "required"),
    });

    const initialValues: IFormValues = useMemo(() => {
        return {
            name: item.role?.name || "",
            permissions: item?.permissions || [],
        };
    }, [item]);

    const handleSubmitForm = (values: IFormValues) => {
        if (item.role?.id) {
            dispatch(updRoles(values, item.role?.id));
        } else {
            dispatch(addRole(values));
        }
    };

    const {
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        resetForm,
    } = useFormik({
        initialValues,
        onSubmit: handleSubmitForm,
        validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    const close = () => {
        dispatch(modalRoles(false));
        resetForm();
    };

    return (
        <Modal isOpen={isOpen} toggle={close} size="xl">
            <ModalHeader toggle={close} tag="h4">
                {item.role?.id ? "Edit" : "Add"} Role
            </ModalHeader>
            <ModalBody>
                <Form className="needs-validation" onSubmit={handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <div className="mb-3">
                                <LabelInput
                                    label="Name*"
                                    placeholder="Name*"
                                    name="name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.name}
                                    hasError={!!(errors.name && touched.name)}
                                    errorText={errors.name}
                                />
                            </div>
                            <div className="mb-3">
                                <span className="d-block mb-2 fw-bold">Roles: </span>
                                {!isLoadingPermissions && permissionsGroups && permissionsGroups.data?.length > 0
                                    && <PermissionsTable groups={permissionsGroups.data} checkedIds={values.permissions}
                                                         onChangeCheckedIds={(t) => setFieldValue('permissions', t)}/>}
                                {isLoadingPermissions && (<NoSearchResult text="Loading permissions..."/>)}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button type="submit" className="btn btn-success save-user">
                                    Save
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default ModalRoles;
